import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect, useCallback } from 'react';
import { LocationOn, Add, UnfoldMore, Save } from '@mui/icons-material';

import { Search, Clear } from '@mui/icons-material';
import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { localidadesAPI } from '../../../core/api/api';
import { useSortableTable } from '../../../shared/hooks/useSortableTable';
import { textMatch } from '../../../shared/utils/textUtils';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const PAGE_SIZE = 50;

const GrillaLocalidades = () => {
  const [localidades, setLocalidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [page, setPage] = useState(1);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [nombre, setNombre] = useState('');
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const { sortedData, requestSort, getSortIcon } = useSortableTable(localidades);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await localidadesAPI.getLocalidades();
      setLocalidades(res.data || []);
    } catch {
      setToast({ message: 'Error al cargar las localidades.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = sortedData.filter(l =>
    textMatch(l.localidad, buscar)
  );
  const totalPages = Math.ceil(filtradas.length / PAGE_SIZE);
  const paginadas = filtradas.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleNueva = () => { setEditando(null); setNombre(''); setMostrarForm(true); window.scrollTo(0, 0); };
  const handleEditar = (loc) => { setEditando(loc); setNombre(loc.localidad); setMostrarForm(true); window.scrollTo(0, 0); };
  const handleCancelar = () => { setMostrarForm(false); setEditando(null); setNombre(''); };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editando) {
        await localidadesAPI.actualizarLocalidad(editando.idLocalidad, nombre.trim());
        setToast({ message: 'Localidad actualizada con éxito.', type: 'success' });
      } else {
        await localidadesAPI.guardarLocalidad(nombre.trim());
        setToast({ message: 'Localidad guardada con éxito.', type: 'success' });
      }
      handleCancelar();
      cargar();
    } catch {
      setToast({ message: 'Error al guardar la localidad.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confirmarEliminar = async () => {
    try {
      await localidadesAPI.eliminarLocalidad(confirmEliminar);
      setToast({ message: 'Localidad eliminada con éxito.', type: 'success' });
      cargar();
    } catch {
      setToast({ message: 'Error al eliminar la localidad.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <LocationOn sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Localidades
          </h2>
          <span className="page-subtitle">Administración de localidades</span>
        </div>

        <Modal show={mostrarForm} onHide={handleCancelar}>
          <Modal.Header closeButton>
            <Modal.Title>{editando ? 'Editar Localidad' : 'Nueva Localidad'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body>
              <Form.Group>
                <Form.Label>* Localidad</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={100}
                  placeholder="Localidad..."
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                  autoFocus
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCancelar} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !nombre.trim()}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {editando ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">Filtros de Búsqueda</h3>
          </div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${buscar ? 'filter-active' : ''}`}>
                <label className="filter-label">Localidad</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Buscar localidad..."
                  value={buscar}
                  onChange={(e) => { setBuscar(e.target.value); setPage(1); }}
                />
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={() => setPage(1)} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={() => { setBuscar(''); setPage(1); }}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left">
              <h3 className="results-title">Resultados</h3>
            </div>
            <div className="results-info">
              <span className="results-count">{filtradas.length} localidades encontradas</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={handleNueva} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nueva Localidad
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('idLocalidad')} style={{ cursor: 'pointer' }}>
                    # {getSortIcon('idLocalidad') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th onClick={() => requestSort('localidad')} style={{ cursor: 'pointer' }}>
                    Localidad {getSortIcon('localidad') || <UnfoldMore sx={{ fontSize: 14, opacity: 0.4 }} />}
                  </th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginadas.length === 0 ? (
                  <tr><td colSpan="3" className="empty-state">{loading ? 'Cargando...' : 'No hay localidades para mostrar'}</td></tr>
                ) : (
                  paginadas.map((loc) => (
                    <tr key={loc.idLocalidad}>
                      <td>{loc.idLocalidad}</td>
                      <td>{loc.localidad}</td>
                      <td className="action-cell">
                        <RowActions onEdit={() => handleEditar(loc)} onDelete={() => setConfirmEliminar(loc.idLocalidad)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </DataTable>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '1rem' }}>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(1)} disabled={page === 1}>«</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>‹</Button>
              <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Página {page} de {totalPages}</span>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>›</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</Button>
            </div>
          )}
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar localidad"
        message="¿Estás seguro que querés eliminar esta localidad?"
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaLocalidades;
